import React, {useCallback, useEffect, useMemo} from 'react';

import {AddedFile} from 'src/components/added-files';
import {validateBulkUploadCrmIdsFile} from 'src/modules/bulk-update/crm-ids-update/utils/parseCSVFileContent';

import {FlatButton} from '@pb/kit-component-button';
import {DropZone} from '@pb/kit-component-dropzone';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons, Sizes} from '@pb/kit-component-icon';
import {Message} from '@pb/kit-component-message';


import {
    MATCHING_LIST,
    MAX_FILE_SIZE,
    UPLOAD_CRM_TEMPLATE_FILE_NAME,
    UPLOAD_FILE_MAX_SIZE_LIMIT_MB,
} from '../../constants';
import {DropZoneView} from '../drop-zone-view';

import {
    uploadFileBlockContent,
    uploadFileBlockDropzone,
    uploadFileBlockHeader,
    uploadFileBlockList,
    uploadFileBlockReviewPartition,
    uploadFileBlockUploadPartition,
} from './upload-file.module.styl';

type Props = {
    addedFile: File | null,
    onLoadFile: (file: File | null) => void,
    setUploadFileError: (error: string) => void,
    uploadFileError: string,
};

export const UploadFileBlock = (props: Props) => {
    const {addedFile, onLoadFile, uploadFileError, setUploadFileError} = props;

    const onDrop = useCallback((accepted: Array<File>) => {
        if (![
            'text/csv',
            'application/vnd.ms-excel',
        ].includes(accepted[0].type)) {
            setUploadFileError('The file format is not supported. Please use an accepted file format.');
            onLoadFile(null);
        } else if (accepted[0].size < 20) {
            setUploadFileError('The uploaded file cannot be empty');
            onLoadFile(null);
        } else if (accepted[0].size > MAX_FILE_SIZE) {

            setUploadFileError(`The maximum file size that can be uploaded is ${UPLOAD_FILE_MAX_SIZE_LIMIT_MB} MB.\n Please ensure your file is less than the maximum size`);
            onLoadFile(null);
        } else {
            setUploadFileError('');
            onLoadFile(accepted[0]);
        }
    }, [onLoadFile, setUploadFileError]);

    useEffect(() => {
        const validateFile = async () => {
            if (addedFile) {
                try {
                    const isValid = await validateBulkUploadCrmIdsFile(addedFile);
                    if (!isValid) {
                        setUploadFileError('File can\'t be parsed. Please make sure the file matches the template.');
                    }
                } catch (error) {
                    if (error instanceof Error) {
                        setUploadFileError(error.message);
                    } else {
                        setUploadFileError('Bulk upload temporarily unavailable. Please try again later.');
                    }
                }
            }
        };

        validateFile();
    }, [addedFile, setUploadFileError]);

    const onDownloadTemplateHandler = () => {
        window.open(UPLOAD_CRM_TEMPLATE_FILE_NAME);
    };

    const addedFilesView = useMemo(() => (
        addedFile && !uploadFileError ?
                <AddedFile addedFile={addedFile} removeFile={() => onLoadFile(null)}/> :
            null
    ), [addedFile, uploadFileError, onLoadFile]);


    return (
        <Group className={uploadFileBlockContent}>
            <div className={uploadFileBlockReviewPartition}>
                <Group gap="l" direction="v">
                    <div>Uploading a list of users will allow PitchBook to match against our database of users.</div>
                    <div>
                        <div className={uploadFileBlockHeader}><b>Matching Criteria:</b></div>
                        <ul className={uploadFileBlockList}>
                            {MATCHING_LIST.map(({caption, required}, index) => (
                                <li key={caption}>
                                    {`${index + 1}.`}&nbsp;&nbsp;
                                    {required ? <b>{caption}</b> : caption}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Group gap="xs" direction="v">
                        <div className={uploadFileBlockHeader}><b>Don't have a list?</b></div>
                        <FlatButton
                            startIcon={<Icon type={Icons.DOWNLOAD} size={Sizes.Size_14}/>}
                            onClick={onDownloadTemplateHandler}>
                            <b>Download a template now to create one.</b>
                        </FlatButton>
                    </Group>
                </Group>
            </div>
            <Group className={uploadFileBlockUploadPartition} direction="v">
                <DropZone multiple={false} onDrop={onDrop} mix={uploadFileBlockDropzone}>
                    <DropZoneView addedFile={addedFile}/>
                </DropZone>
                {addedFilesView}
                {uploadFileError ? (
                    <Message color="error" iconProps={{type: Icons.ERROR, size: Sizes.Size_14}}>
                        {uploadFileError}
                    </Message>
                ) : null}
            </Group>
        </Group>
    );
};
